import { buildClearCookie, buildLogoutCookies } from '../session/cookies.js';
import type { CookieMutation, CookieStore, ResolvedConfig } from '../types.js';
import { COOKIE_OAUTH_STATE, COOKIE_REFRESH_TOKEN, COOKIE_SESSION } from '../constants.js';

export interface CookieHeaderWriter {
  setHeader(name: string, value: string | string[]): void;
  appendHeader?(name: string, value: string): void;
}

export function cookieStoreFromHeader(cookieHeader: string | null | undefined): CookieStore {
  const parsed = parseCookieHeader(cookieHeader);
  return {
    get(name: string) {
      return parsed[name];
    },
  };
}

export function parseCookieHeader(cookieHeader: string | null | undefined): Record<string, string> {
  if (!cookieHeader) return {};
  const result: Record<string, string> = {};
  for (const part of cookieHeader.split(';')) {
    const [rawName, ...rest] = part.trim().split('=');
    if (!rawName) continue;
    result[rawName] = decodeURIComponent(rest.join('='));
  }
  return result;
}

export function formatSetCookieHeader(mutation: CookieMutation): string {
  const { name, value, options } = mutation;
  const parts = [
    `${name}=${value}`,
    `Path=${options.path}`,
    'HttpOnly',
    `SameSite=${capitalizeSameSite(options.sameSite)}`,
    `Max-Age=${options.maxAge}`,
  ];
  if (options.secure || options.partitioned) parts.push('Secure');
  if (options.partitioned) parts.push('Partitioned');
  return parts.join('; ');
}

function capitalizeSameSite(value: 'lax' | 'strict' | 'none'): string {
  if (value === 'lax') return 'Lax';
  if (value === 'strict') return 'Strict';
  return 'None';
}

export function clearCookieMutations(config: ResolvedConfig, names: string[]): CookieMutation[] {
  return names.map((name) => buildClearCookie(config, name));
}

export function applyCookieMutations(
  writer: CookieHeaderWriter,
  mutations: CookieMutation[],
): void {
  if (mutations.length === 0) return;

  const formatted = mutations.map((mutation) => formatSetCookieHeader(mutation));
  if (writer.appendHeader) {
    for (const value of formatted) {
      writer.appendHeader('Set-Cookie', value);
    }
    return;
  }

  writer.setHeader('Set-Cookie', formatted);
}

export function clearCookiesOnResponse(
  config: ResolvedConfig,
  writer: CookieHeaderWriter,
  names?: string[],
): void {
  const mutations = names
    ? clearCookieMutations(config, names)
    : buildLogoutCookies(config);
  applyCookieMutations(writer, mutations);
}

export function readAuthCookies(cookieHeader: string | null | undefined) {
  const store = cookieStoreFromHeader(cookieHeader);
  return {
    session: store.get(COOKIE_SESSION) ?? null,
    refreshToken: store.get(COOKIE_REFRESH_TOKEN) ?? null,
    oauthState: store.get(COOKIE_OAUTH_STATE) ?? null,
    store,
  };
}

export { COOKIE_OAUTH_STATE, COOKIE_REFRESH_TOKEN, COOKIE_SESSION };
